import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import type { TablePaginationConfig } from 'antd';
import type { TableCurrentDataSource } from 'antd/lib/table/interface';
import {
  Button,
  Card,
  FilteredTable,
  Flex,
  Gap,
  Gutter,
  Text,
} from '@common/components';
import { ShiftTypeWithIcon } from '@jobs/components';
import { toHumanTimeFormat, toUSFormat } from 'works/legacy/lib/date-helper';
import type { SelectedGapEntryType } from './_bulk-shift-step-types';

const PANEL_PAGE_SIZE = 100;

export function BulkShiftStepSelectShiftsPanel({
  selectedGaps,
  onClearAll,
  onRemove,
}: {
  selectedGaps: SelectedGapEntryType[];
  onClearAll: () => void;
  onRemove: (shiftGapId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [panelPage, setPanelPage] = useState(1);

  // Clamp the current page when removals push it past the new last page so the
  // user is not stranded on an empty slice after pruning the selection.
  useEffect(() => {
    if (selectedGaps.length === 0) {
      if (panelPage !== 1) setPanelPage(1);
      return;
    }
    const lastPage = Math.max(
      1,
      Math.ceil(selectedGaps.length / PANEL_PAGE_SIZE)
    );
    if (panelPage > lastPage) setPanelPage(lastPage);
  }, [selectedGaps.length, panelPage]);

  if (selectedGaps.length === 0) return null;

  const count = selectedGaps.length;
  const countLabel =
    count === 1 ? '1 shift selected' : `${count} shifts selected`;

  const columns = [
    {
      title: 'Start time',
      key: 'startDatetime',
      sorter: (a: SelectedGapEntryType, b: SelectedGapEntryType) =>
        a.startDatetime.localeCompare(b.startDatetime),
      render: (_: unknown, record: SelectedGapEntryType) => {
        const startTime = moment
          .parseZone(record.startDatetime)
          .tz(record.timeZone);
        return (
          <>
            <Text>{toUSFormat(startTime)}</Text>
            <br />
            <Text bold>{toHumanTimeFormat(startTime)}</Text>
          </>
        );
      },
    },
    {
      title: 'Length',
      dataIndex: 'shiftDuration',
      key: 'shiftDuration',
      sorter: (a: SelectedGapEntryType, b: SelectedGapEntryType) =>
        Number(a.shiftDuration) - Number(b.shiftDuration),
      render: (shiftDuration: string) => `${shiftDuration} hrs`,
    },
    {
      title: 'Period',
      key: 'timeOfDay',
      render: (_: unknown, record: SelectedGapEntryType) => (
        <ShiftTypeWithIcon value={record.timeOfDay ?? undefined} />
      ),
    },
    {
      title: 'Unit / Cost Center',
      key: 'organizationUnit',
      sorter: (a: SelectedGapEntryType, b: SelectedGapEntryType) =>
        a.organizationUnit.name.localeCompare(b.organizationUnit.name),
      render: (_: unknown, record: SelectedGapEntryType) => (
        <>
          <Text>{record.organizationUnit.name}</Text>
          <br />
          <Text secondary>{record.organizationUnit.costCenter ?? ''}</Text>
        </>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      sorter: (a: SelectedGapEntryType, b: SelectedGapEntryType) =>
        (a.role?.abbreviation ?? '').localeCompare(b.role?.abbreviation ?? ''),
      render: (_: unknown, record: SelectedGapEntryType) =>
        record.role?.abbreviation ?? '--',
    },
    {
      title: '',
      key: 'remove',
      width: 96,
      render: (_: unknown, record: SelectedGapEntryType) => (
        <Button
          kind="link"
          size="sm"
          onClick={() => onRemove(record.shiftGapId)}
          dataTestId={`bulk-shift-selection-panel-remove-${record.shiftGapId}`}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Card data-testid="bulk-shift-selection-panel">
      <Flex justify="space-between" align="center" fullWidth>
        <Gap size="xs" align="center">
          <Text bold size="md" dataTestId="bulk-shift-selection-panel-count">
            {countLabel}
          </Text>
          <Button
            kind="link"
            size="md"
            onClick={() => setExpanded((value) => !value)}
            dataTestId="bulk-shift-selection-panel-toggle"
          >
            — {expanded ? 'Hide' : 'Show'}
          </Button>
        </Gap>
        <Button
          kind="link"
          size="md"
          onClick={onClearAll}
          dataTestId="bulk-shift-selection-panel-clear"
        >
          Clear all
        </Button>
      </Flex>
      {expanded && (
        <Gutter size="md" direction="top">
          <FilteredTable
            rowKey="shiftGapId"
            dataSource={selectedGaps}
            columns={columns}
            filters={{}}
            pagination={{
              current: panelPage,
              pageSize: PANEL_PAGE_SIZE,
              defaultPageSize: PANEL_PAGE_SIZE,
              total: selectedGaps.length,
            }}
            onChange={(
              nextPagination: TablePaginationConfig,
              _filters: unknown,
              _sorter: unknown,
              extra: TableCurrentDataSource<SelectedGapEntryType>
            ) => {
              if (extra?.action === 'sort') {
                setPanelPage(1);
                return;
              }
              setPanelPage(nextPagination.current ?? 1);
            }}
          />
        </Gutter>
      )}
    </Card>
  );
}
